import React, { useState } from 'react';
import axios from 'axios';
import { Mail, User, Phone, MapPin, Ruler, Weight, Calendar } from 'lucide-react';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    phone: '',
    email: '',
    date_of_birth: '',
    height: '',
    weight: '',
    positions: '',
    high_school: '',
    graduation_year: '',
    additional_info: ''
  });

  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const API = `${BACKEND_URL}/api`;

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      const response = await axios.post(`${API}/contact`, formData);
      console.log('Form submitted successfully:', response.data);
      setSubmitted(true);
      
      // Reset form after 5 seconds
      setTimeout(() => {
        setSubmitted(false);
        setFormData({
          name: '',
          address: '',
          phone: '',
          email: '',
          date_of_birth: '',
          height: '',
          weight: '',
          positions: '',
          high_school: '',
          graduation_year: '',
          additional_info: ''
        });
      }, 5000);
    } catch (err) {
      console.error('Error submitting form:', err);
      setError('Failed to submit form. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="page-content">
      <section className="page-hero">
        <div className="container">
          <h1 className="display-large">Get In Touch</h1>
          <p className="body-large">Join the Pacific Ghost family - fill out the form below</p>
        </div>
      </section>

      <section className="contact-section">
        <div className="container">
          <div className="contact-grid">
            {/* Contact Form */}
            <div className="contact-form-wrapper network-card">
              <h2 className="heading-2">Player Registration</h2>
              {submitted ? (
                <div className="success-message">
                  <h3>Thank you for your interest!</h3>
                  <p>We've received your registration and will be in touch soon.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="contact-form">
                  {error && (
                    <div className="error-message" style={{padding: '16px', background: '#fee', borderRadius: '12px', marginBottom: '16px', color: '#c00'}}>
                      {error}
                    </div>
                  )}
                  
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="name">
                        <User size={18} /> Full Name *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        placeholder="Enter full name"
                        disabled={submitting}
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="email">
                        <Mail size={18} /> Email Address *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        placeholder="your@email.com"
                        disabled={submitting}
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="phone">
                        <Phone size={18} /> Phone Number *
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        placeholder="(555) 555-5555"
                        disabled={submitting}
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="date_of_birth">
                        <Calendar size={18} /> Date of Birth *
                      </label>
                      <input
                        type="date"
                        id="date_of_birth"
                        name="date_of_birth"
                        value={formData.date_of_birth}
                        onChange={handleChange}
                        required
                        disabled={submitting}
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="address">
                        <MapPin size={18} /> Address *
                      </label>
                      <input
                        type="text"
                        id="address"
                        name="address"
                        value={formData.address}
                        onChange={handleChange}
                        required
                        placeholder="City, State"
                        disabled={submitting}
                      />
                    </div>
                  </div>

                  <div className="form-row two-col">
                    <div className="form-group">
                      <label htmlFor="height">
                        <Ruler size={18} /> Height *
                      </label>
                      <input
                        type="text"
                        id="height"
                        name="height"
                        value={formData.height}
                        onChange={handleChange}
                        required
                        placeholder="5'10&quot;"
                        disabled={submitting}
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="weight">
                        <Weight size={18} /> Weight *
                      </label>
                      <input
                        type="text"
                        id="weight"
                        name="weight"
                        value={formData.weight}
                        onChange={handleChange}
                        required
                        placeholder="165 lbs"
                        disabled={submitting}
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="positions">Position(s) *</label>
                      <input
                        type="text"
                        id="positions"
                        name="positions"
                        value={formData.positions}
                        onChange={handleChange}
                        required
                        placeholder="SS/P, C/3B, etc."
                        disabled={submitting}
                      />
                    </div>
                  </div>

                  <div className="form-row two-col">
                    <div className="form-group">
                      <label htmlFor="high_school">High School *</label>
                      <input
                        type="text"
                        id="high_school"
                        name="high_school"
                        value={formData.high_school}
                        onChange={handleChange}
                        required
                        placeholder="Current or future high school"
                        disabled={submitting}
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="graduation_year">Graduation Year *</label>
                      <select
                        id="graduation_year"
                        name="graduation_year"
                        value={formData.graduation_year}
                        onChange={handleChange}
                        required
                        disabled={submitting}
                      >
                        <option value="">Select Year</option>
                        <option value="2026">2026</option>
                        <option value="2027">2027</option>
                        <option value="2028">2028</option>
                        <option value="2029">2029</option>
                        <option value="2030">2030</option>
                        <option value="2031">2031</option>
                        <option value="2032">2032</option>
                      </select>
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="additional_info">Additional Information</label>
                      <textarea
                        id="additional_info"
                        name="additional_info"
                        value={formData.additional_info}
                        onChange={handleChange}
                        rows="4"
                        placeholder="Tell us anything else you'd like us to know..."
                        disabled={submitting}
                      />
                    </div>
                  </div>

                  <button type="submit" className="btn-primary btn-submit" disabled={submitting}>
                    {submitting ? 'Submitting...' : 'Submit Registration'}
                  </button>
                </form>
              )}
            </div>

            {/* Contact Info */}
            <div className="contact-info">
              <div className="network-card">
                <h3 className="network-card-title">Contact Information</h3>
                <div className="contact-details">
                  <div className="contact-item">
                    <Mail size={24} />
                    <div>
                      <strong>Email</strong>
                      <a href="mailto:info@pacificghostbaseball.com">info@pacificghostbaseball.com</a>
                      <a href="mailto:jeremy@pacificghostbaseball.com">jeremy@pacificghostbaseball.com</a>
                    </div>
                  </div>
                  <div className="contact-item">
                    <MapPin size={24} />
                    <div>
                      <strong>Location</strong>
                      <p>California</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="network-card">
                <h3 className="network-card-title">What Happens Next?</h3>
                <ol className="next-steps">
                  <li>Submit your registration form</li>
                  <li>Our team reviews your information</li>
                  <li>We'll contact you within 48 hours</li>
                  <li>Schedule an evaluation or tryout</li>
                </ol>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;